import { defineStore } from "pinia";
import type { LookupServiceProviderType, LookupServiceResponseType, QsoType } from "@/types";
import { useProfileStore } from "@/stores/ProfileStore";
import LookupService from "@/services/LookupService";
import QsoService from "@/services/QsoService";
import { lookupsLogger } from "@/utils/loggers";
import { useSettingStore } from '@/stores/SettingStore';
import { useOnline } from '@vueuse/core'
import { ProviderAuthenticationError, ProviderSessionExpiredError, ProviderUnavailableError, LookupsDisabledError } from "@/utils/errors/LookupServiceErrors";
export const useCallsignLookupsStore = defineStore("lookups", {
  state: () => ({
    sessionKey: "" as string,
    // Which provider the current sessionKey was issued for. A key from a
    // previously selected provider must not be trusted after switching, so we
    // track its origin and force reauthentication on a mismatch.
    sessionKeyProvider: null as LookupServiceProviderType | null,
    client: null as LookupService | null,
    lastLookup: null as LookupServiceResponseType | null,
    lastLookupPreviousQsos: [] as QsoType[],
  }),

  actions: {
    setClient(provider: LookupServiceProviderType) {
      // Some providers (e.g. OFFLINE) have no online adapter. Rather than letting
      // the constructor throw -- which would abort profile activation -- treat
      // them as "no client" and let lookups fall back to the local operator DB
      // via useCallsignLookup. We check support up-front instead of catching, so
      // a genuine construction failure for a supported provider still surfaces
      // rather than being silently swallowed as a later "Client not initialized".
      if (!LookupService.isSupported(provider)) {
        lookupsLogger(
          `No online lookup adapter for provider "${provider}"; offline lookups only`
        );
        this.client = null;
        return;
      }
      this.client = new LookupService(provider);
    },
    setSessionKey(sessionKey: string) {
      this.sessionKey = sessionKey;
    },
    async initProvider(provider: LookupServiceProviderType) {
      try {
        lookupsLogger('Initializing provider:', provider);
        const { username, password } = this.getCredentialForProvider(provider);
        this.setClient(provider);
        if (
          this.client &&
          this.client.requiresAuthentication &&
          username &&
          password
        ) {
          try {
            const sessionKey = await this.client.authenticate(username, password);
            lookupsLogger(`Initialized: ${provider}`);
            this.setSessionKey(sessionKey);
            this.sessionKeyProvider = provider;
          } catch (error) {
            console.error("Error during authentication:", error);
          }
        } else {
          lookupsLogger(`Initialized: ${provider}`);
        }
      } catch (initError) {
        console.error("Provider initialization error:", initError);
        throw initError; // Re-throw if you want the error propagated or handle it here
      }
    },

    clearLastLookup() {
      this.lastLookup = null;
      this.clearLastLookupPreviousQsos();
    },

    clearLastLookupPreviousQsos() {
      this.lastLookupPreviousQsos = [];
    },

    async findPreviousQsos(callsign?: string) {
      const targetCallsign = callsign ?? this.lastLookup?.callsign;
      if (!targetCallsign) return [];
      const previousQsos = await QsoService.findAllByCallsign(targetCallsign)
      // Only update shared state if this is still the most recent lookup -
      // a concurrent lookup may have already updated lastLookup to a different callsign.
      if (this.lastLookup?.callsign === targetCallsign) {
        this.lastLookupPreviousQsos = previousQsos || [];
      }
      return previousQsos;
    },
    async lookupCallsign(callsign: string, ignoreDoLookupsSetting = false) {
      this.clearLastLookup();
      const online = useOnline();
      const provider = this.client?.providerName ?? useSettingStore().lookupProvider;
      if (!useSettingStore().doLookups && !ignoreDoLookupsSetting) {
        lookupsLogger('Skipping callsign lookup as it is disabled in settings');
        throw new LookupsDisabledError(provider);
      }
      if (!this.client) {
        throw new Error("Client not initialized");
      }

      // Only providers that need the network are blocked when offline. An
      // offline provider (the local operator DB) still serves lookups while the
      // device is offline, so it must not short-circuit here.
      if (this.client.requiresConnectivity && !online.value) {
        lookupsLogger('Skipping callsign lookup as the user is offline');
        throw new ProviderUnavailableError(provider, "You're offline. Callsign lookups are unavailable.");
      }

      lookupsLogger('Looking up callsign:', callsign);

      // Establish a session up-front when the provider needs auth but we have no
      // valid session key for it -- either none at all, or one issued for a
      // different provider that we just switched away from. The key is in-memory
      // only (lost on reload) and boot-time auth can fail silently, so a nil key
      // here is expected, not exceptional. reauthenticate() throws
      // ProviderAuthenticationError on failure, which the UI surfaces as a toast
      // rather than reporting to Sentry.
      if (
        this.client.requiresAuthentication &&
        (!this.sessionKey || this.sessionKeyProvider !== this.client.providerName)
      ) {
        lookupsLogger('No valid session key for provider; authenticating before lookup');
        await this.reauthenticate();
      }

      try {
        return await this.performLookup(callsign);
      } catch (error) {
        if (error instanceof ProviderSessionExpiredError) {
          // Stale session (e.g. QRZ 0Z rollover): get a fresh key and retry once.
          lookupsLogger('Session expired, re-authenticating and retrying lookup');
          await this.reauthenticate();
          return await this.performLookup(callsign);
        }
        console.error("Lookup failed:", error);
        throw error; // Re-throw other errors
      }
    },

    async performLookup(callsign: string) {
      if (!this.client) {
        throw new Error("Client not initialized");
      }
      const lookupResult = await this.client.lookupCallsign(callsign, this.sessionKey);
      this.lastLookup = lookupResult;
      await this.findPreviousQsos(lookupResult.callsign);
      return lookupResult; // Return local result, not this.lastLookup
    },

    // Obtain a fresh session key for the active provider. Always throws a typed
    // ProviderAuthenticationError on failure (missing credentials or a failed
    // authenticate call) so callers can show a user-facing toast instead of
    // leaking a generic exception to Sentry.
    async reauthenticate(): Promise<void> {
      if (!this.client) {
        throw new Error("Client not initialized");
      }
      const provider = this.client.providerName;
      const { username, password } = this.getCredentialForProvider(provider);
      if (!username || !password) {
        throw new ProviderAuthenticationError(provider, "Authentication credentials are missing");
      }
      try {
        this.sessionKey = await this.client.authenticate(username, password);
        this.sessionKeyProvider = provider;
      } catch (error) {
        console.error("Re-authentication failed:", error);
        // Preserve typed errors: an unreachable service is a connectivity issue,
        // not bad credentials, and shouldn't be relabeled as an auth failure.
        if (
          error instanceof ProviderAuthenticationError ||
          error instanceof ProviderUnavailableError
        ) {
          throw error;
        }
        throw new ProviderAuthenticationError(provider, "Unable to authenticate");
      }
    },

    getCredentialForProvider(provider: LookupServiceProviderType): {
      username?: string;
      password?: string;
    } {
      const profileStore = useProfileStore();
      const { activeProfile } = profileStore;

      const credentials: {
        username?: string;
        password?: string;
      } = { username: undefined, password: undefined };

      if (!activeProfile) {
        return credentials;
      }
      switch (provider) {
        case "QRZ":
          credentials.username = activeProfile.qrz.username;
          credentials.password = activeProfile.qrz.password;
          break;
        case "HAMQTH":
          credentials.username = activeProfile.hamQth.username;
          credentials.password = activeProfile.hamQth.password;
          break;

      }

      return credentials;
    },
  },
});
